/**
 * 회사 상세 「고객 지원을 위한 접근」 카드·「지원 계정으로 접속」·동의 스위치·잠금 화면 규칙
 * (W6 A3~A5 — 계약 SUPPORT 2·3·5·9, support-scope.ts, 설계 3-1·3-2·3-3·3-5, CONSENT-1028).
 * Vue·axios 를 들이지 않는 순수 모듈이라 node --test 로 시험한다(`__tests__/supportAccess.test.mjs`).
 */
import { can } from "./perm.js";

/** 진입 사유(계약 SUPPORT_REASON_CODES, OTHER 는 메모 필수) */
/** 전환 스위치 supportAccess(introspect modes)가 켜졌나 — 꺼진 동안(배포 날)은 지원 접속·동의 상자를 숨겨 main 과 같게(리셀러 콘솔과 같은 규칙). 모르면 꺼짐 */
export const supportAccessOn = (introspect) => !!(introspect && introspect.modes && introspect.modes.supportAccess === "on");

export const SUPPORT_REASONS = [
  { value: "CUSTOMER_REQUEST", label: "고객 문의" },
  { value: "ONBOARDING", label: "초기 설정" },
  { value: "INCIDENT", label: "장애" },
  { value: "SECURITY", label: "보안" },
  { value: "OTHER", label: "기타" },
];

/** 진입 앱(계약 SUPPORT_APPS) */
export const APP_LABEL = {
  studio: "스튜디오",
  "admin-hospital": "병원 관리자 페이지",
  "admin-manufacturer": "제조사 관리자 페이지",
  "admin-reseller": "리셀러 콘솔",
};

/** 회사 종류 → 고를 수 있는 앱 */
export const appsForCompanyType = (type) => {
  if (type === "HOSPITAL") return ["studio", "admin-hospital"];
  if (type === "MANUFACTURER" || type === "DENTAL_LABORATORY") return ["studio", "admin-manufacturer"];
  if (type === "STUDIO") return ["studio"];
  // 리셀러 회사는 리셀러 콘솔로만, 동의 없이(사용자 결정 10-29)
  if (type === "RESELLER") return ["admin-reseller"];
  return [];
};

/** 앱 → 주소 env(CI-06: 비밀 아닌 키 4개, 추적 env 전부) */
export const APP_URL_ENV = {
  studio: "VUE_APP_STUDIO_URL",
  "admin-hospital": "VUE_APP_HOSPITAL_ADMIN_URL",
  "admin-manufacturer": "VUE_APP_MANUFACTURER_ADMIN_URL",
  "admin-reseller": "VUE_APP_RESELLER_URL",
};

/**
 * 화면 env(빌드 때 값이 박힘 — webpack 은 process.env.VUE_APP_* 를 글자 그대로 쓴 곳만 바꿈).
 * APP_URL_ENV 와 같은 키여야 함(시험이 확인 — 10-29 intest I4: 리셀러 주소를 빠뜨려 새 탭이 안 열림)
 */
export const appUrlEnv = () => ({
  VUE_APP_STUDIO_URL: process.env.VUE_APP_STUDIO_URL,
  VUE_APP_HOSPITAL_ADMIN_URL: process.env.VUE_APP_HOSPITAL_ADMIN_URL,
  VUE_APP_MANUFACTURER_ADMIN_URL: process.env.VUE_APP_MANUFACTURER_ADMIN_URL,
  VUE_APP_RESELLER_URL: process.env.VUE_APP_RESELLER_URL,
});

/** 새 탭 주소 https://<앱>/support/enter#c=<code>. 주소 env 가 없으면 null */
export const supportEnterUrl = (app, code, env = {}) => {
  const base = String(env[APP_URL_ENV[app]] || "").replace(/\/+$/, "");
  return base && code ? `${base}/support/enter#c=${encodeURIComponent(code)}` : null;
};

/** 본인 확인 안내(운영자·리셀러 8시간 — 10-29 사용자 결정) */
export const PROOF_HINT = "지원 접속 전에 본인 확인을 합니다(8시간 동안 다시 묻지 않음). 인증번호는 내 2차 인증 수단으로 갑니다.";
export const CONSENT_PROOF_HINT = "바꾸기 전에 본인 확인을 합니다(8시간 동안은 지원 접속도 다시 묻지 않음). 인증번호는 내 2차 인증 수단으로 갑니다.";

export const ENTRY_HELP =
  "새 탭에서 열립니다. 15분마다 저절로 이어지고, 30분 쓰지 않거나 8시간이 지나면 끝납니다. 끝낼 때는 탭 맨 위 [종료].";

const isOn = (consent) => !!consent && consent.status === "ON";
const subOf = (status, kind) => ((status && status.subAccounts) || []).find((s) => s && s.kind === kind) || null;

/** 동의 이름(서버 text.title 이 없을 때만) */
export const CONSENT_TITLE = "고객 지원을 위한 접근 허용";
export const consentTitleOf = (consent) => (consent && consent.text && consent.text.title) || CONSENT_TITLE;

/**
 * 동의를 끄면 서버가 거는 자동 잠금(lockSource AUTO). 다시 켠 뒤 접속하면 서버가 같은 계정을 풀어 쓴다(support-account ensure).
 * lockSource 가 없는 옛 응답이면 사유 글자로 가름
 */
const AUTO_LOCK_REASON = "CONSENT_REVOKED";
const autoLocked = (sub) => (sub.lockSource ? sub.lockSource === "AUTO" : sub.lockReason === AUTO_LOCK_REASON);

/**
 * GET /admin/companies/:companyId/support 응답 + 권한 → 보일 버튼(A3 표).
 * perms 칸이 없으면(옛 inapi) 숨기지 않음 — 서버가 판정(ops.support.* 는 배포일부터 서버가 막음)
 * 서면 동의 등록·동의 철회 기록 버튼은 없앰(CONSENT-1028 — 스위치로 대신, 서버 API 는 남음)
 */
export const cardButtons = (status, perms) => {
  const consent = status && status.consent;
  // 리셀러 회사는 동의가 없음(consentRequired false) — 스위치를 그리지 않음
  const consentRequired = !(status && status.consentRequired === false);
  const sub = subOf(status, "CERTILIFE");
  const locked = !!(sub && sub.lockedAt);
  const edit = can(perms, "ops.support.edit");
  const manage = can(perms, "ops.support.manage");
  return {
    // 동의가 꺼져 있어도 보임(옆에 안내, 누르면 서버 문구). 동의 철회 자동 잠금은 다시 켜고 접속하면 풀리므로 숨기지 않음
    enter: !(locked && !autoLocked(sub)) && edit,
    // 동의 스위치 = 진입과 같은 권한(서버 PUT /support/companies/:id/consent 도 ops.support.edit)
    consent: edit && consentRequired,
    evidence: isOn(consent) && manage,
    lock: !!sub && !locked && manage,
    unlock: locked && (isOn(consent) || !consentRequired) && manage,
    auditLog: can(perms, "ops.audit.view"),
  };
};

const day = (iso) => {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? "" : new Date(t + 9 * 3600 * 1000).toISOString().slice(0, 10);
};
export const EVIDENCE_TYPES = [
  { value: "SIGNED_FORM", label: "서명한 동의서" },
  { value: "EMAIL", label: "이메일" },
  { value: "CONTRACT_CLAUSE", label: "계약 조항" },
];
const evidenceLabel = (v) => (EVIDENCE_TYPES.find((e) => e.value === v) || {}).label || v;

/**
 * 켠 쪽 문구(CONSENT-1028 표). 꺼짐이면 끈 쪽(revokedBy — 관리자 이름 또는 서티라이프·리셀러 회사 이름)과 날
 */
export const consentByText = (consent) => {
  if (!consent) return "";
  const name = consent.consentedBy && consent.consentedBy.name;
  if (!isOn(consent)) {
    if (consent.needsChoice) return "새로 가입한 회사라 고객사 관리자가 아직 고르지 않았어요";
    const by = consent.revokedBy && consent.revokedBy.name;
    const when = consent.revokedAt ? `${day(consent.revokedAt)}에 ` : "";
    if (!by) return consent.revokedAt ? `${when}꺼졌어요` : "";
    return consent.revokeSource === "IN_APP" ? `${when}${by} 님이 껐어요` : `${when}${by}에서 껐어요`;
  }
  if (consent.source === "EXISTING") return "기존 가입 회사라 기본으로 켜져 있어요";
  if (consent.source === "IN_APP") return name ? `${name} 님이 켰어요` : "고객사 관리자가 켰어요";
  if (consent.source === "OPERATOR") return `${name || "서티라이프"}에서 유지보수 계약으로 켰어요`;
  if (consent.source === "RESELLER") return `${name || "리셀러"}에서 유지보수 계약으로 켰어요`;
  if (consent.source === "OFFLINE") return "서면 동의로 켜졌어요";
  return "";
};

/** 동의 줄: 켜짐/꺼짐 + 켠 쪽 + 날짜·문구 판 + 서면 증빙 줄 */
export const consentLine = (consent) => {
  const on = isOn(consent);
  const by = consentByText(consent);
  if (!on) return { on, text: "꺼짐", by, detail: "", offline: "" };
  const ev = consent.evidence;
  return {
    on,
    text: "켜짐",
    by,
    detail: [
      day(consent.consentedAt),
      consent.textVersion && `문구 ${consent.textVersion}`,
    ]
      .filter(Boolean)
      .join(" · "),
    offline:
      consent.source === "OFFLINE"
        ? ["서면", ev && ev.receivedAt && `받은 날 ${day(ev.receivedAt)}`, ev && ev.type && `종류: ${evidenceLabel(ev.type)}`]
            .filter(Boolean)
            .join(" · ")
        : "",
  };
};

/** 서브 계정 줄 문장 */
export const subAccountLine = (sub, kind = "CERTILIFE") => {
  if (!sub) return kind === "CERTILIFE" ? "아직 없음(첫 지원 접속 때 만들어짐)" : "아직 없음";
  if (sub.lockedAt) {
    const why = autoLocked(sub) ? "허용이 꺼져서 — 다시 켜고 접속하면 풀림" : sub.lockReason;
    return `잠김(${[why, day(sub.lockedAt)].filter(Boolean).join(" · ")})`;
  }
  return "사용 중";
};

export const subAccountsOf = (status) => ({
  certilife: subOf(status, "CERTILIFE"),
  reseller: subOf(status, "RESELLER"),
  resellerLinked: !!(status && status.resellerLinked),
});

const trimmed = (v) => String(v ?? "").trim();

/** 「지원 계정으로 접속」 입력 검사 */
export const entryError = (form = {}, companyType) => {
  if (!appsForCompanyType(companyType).includes(form.app)) return "들어갈 앱을 고르세요.";
  if (!SUPPORT_REASONS.some((r) => r.value === form.reasonCode)) return "사유를 고르세요.";
  if (form.reasonCode === "OTHER" && !trimmed(form.reasonNote)) return "기타 사유는 메모를 적어 주세요.";
  return "";
};

/**
 * 본인 확인(계약 20de33c3d): step-up challenge·verify({purpose:'LOGIN'})로 받은 증표를 POST /support/proof 에.
 * 운영자는 늘 mfa(리셀러만 2FA 없을 때 password).
 */
export const PROOF_PURPOSE = "LOGIN";
export const proofBody = (mfaToken) => ({ method: "mfa", mfaToken });

/**
 * 본인 확인 단계를 건너뛸까(T-CONTRACT-1031 §1, 사용자 결정 10-31): GET /auth/profile mfa.supportProofRequired 가 false 면
 * 그 사람은 2차 인증을 쓰지 않음 → 인증번호 없이 같은 요청을 다시. 칸이 없으면(옛 inapi) 지금처럼 수단이 있을 때만 본인 확인
 */
export const proofSkipped = (mfa) => !!mfa && mfa.supportProofRequired === false;

/** POST /support/sessions 본문 */
export const sessionBody = (companyId, form) => {
  const body = { companyId, app: form.app, reasonCode: form.reasonCode };
  const note = trimmed(form.reasonNote);
  if (note) body.reasonNote = note;
  return body;
};

const ENTRY_MESSAGES = {
  SUPPORT_SUB_ACCOUNT_LOCKED: "지원 계정이 잠겨 있습니다.",
  SUPPORT_ACCESS_OFF: "지원 접속이 아직 켜지지 않았습니다(보안 설정).",
  MFA_ENROLL_REQUIRED: "이 고객사는 2차 인증 필수입니다. 「내 계정 보안」에서 수단을 먼저 등록하세요.",
  PERMISSION_DENIED: "지원 접속 권한(ops.support.edit)이 없습니다.",
  AUDIT_UNAVAILABLE: "기록 장치 문제로 접속하지 않았습니다. 잠시 후 다시 시도해 주세요.",
};

export const PROOF_NEEDS_FACTOR_TEXT =
  "본인 확인은 2차 인증 수단이 필요합니다(2차 인증 예외로는 확인할 수 없음). 「내 계정 보안」에서 수단을 등록하세요.";
export const PROOF_NOT_APPLIED_TEXT = "본인 확인이 반영되지 않았습니다. 창을 닫고 다시 시도하세요.";

// POST /support/proof 실패 = 403 SUPPORT_PROOF_REQUIRED + reason(W1 support-proof.service.ts). 리셀러 콘솔과 같은 글자
const PROOF_REASON_TEXT = {
  MFA_EXEMPT: PROOF_NEEDS_FACTOR_TEXT,
  MFA_STALE: "인증한 지 오래되었습니다. 인증번호를 다시 받아 확인하세요.",
  MFA_TOKEN_INVALID: "본인 확인이 맞지 않습니다. 인증번호를 다시 받아 확인하세요.",
  PASSWORD_MISMATCH: "비밀번호가 맞지 않습니다.",
  PASSWORD_NOT_ALLOWED: "2차 인증 수단이 있는 계정은 인증번호로 확인하세요.",
};

/** /support/proof 실패 문구. 이유가 없거나 모르면 수단 필요 문구, 다른 코드는 진입 문구 표 또는 null */
export const proofErrorText = (error) => {
  const d = error?.response?.data || {};
  if (d.code === "SUPPORT_PROOF_REQUIRED") return PROOF_REASON_TEXT[d.reason] || PROOF_NEEDS_FACTOR_TEXT;
  return ENTRY_MESSAGES[d.code] || null;
};

/** 동의가 꺼진 회사에 접속(403 SUPPORT_CONSENT_REQUIRED) — 서버 문구가 없을 때만 이 글자 */
export const CONSENT_OFF_TEXT = "이 회사는 「고객 지원을 위한 접근 허용」이 꺼져 있습니다.";

/**
 * POST /support/sessions·PUT 동의 스위치·/support/proof 실패 → 다음 동작(A4 표).
 * PROOF = 본인 확인 뒤 한 번만 다시 보냄 · MESSAGE = 창 안 문구 · null = 일반 실패
 * @param proofDone 이미 본인 확인을 한 뒤의 실패면 true(다시 묻지 않음 — 예외 증표는 서버가 거절, 계약 SUPPORT 9)
 */
export const entryNextStep = (error, proofDone = false) => {
  const data = error?.response?.data || {};
  const code = data.code;
  if (code === "SUPPORT_PROOF_REQUIRED") return proofDone ? { kind: "MESSAGE", text: PROOF_NOT_APPLIED_TEXT } : { kind: "PROOF" };
  if (code === "SUPPORT_CONSENT_REQUIRED")
    return { kind: "MESSAGE", text: (typeof data.message === "string" && data.message) || CONSENT_OFF_TEXT };
  if (ENTRY_MESSAGES[code]) return { kind: "MESSAGE", text: ENTRY_MESSAGES[code] };
  return null;
};

// ── 동의 스위치(CONSENT-1028): PUT /support/companies/:companyId/consent {status, reason} ──
export const CONSENT_REASON_MAX = 200;
const switchVerb = (status) => (status === "ON" ? "켜기" : "끄기");
export const consentSwitchError = (form = {}) => {
  const reason = trimmed(form.reason);
  if (!reason) return `${switchVerb(form.status)} 사유를 적으세요.`;
  return reason.length > CONSENT_REASON_MAX ? `사유는 ${CONSENT_REASON_MAX}자까지입니다.` : "";
};
export const consentSwitchBody = (form) => ({ status: form.status === "ON" ? "ON" : "OFF", reason: trimmed(form.reason) });
/** 고객사에는 메일을 보내지 않음(11-03 사용자) — 고객사 이용 내역에만 남음 */
export const consentDoneText = (status) => (status === "ON" ? "켰어요." : "껐어요.");

// ── 서면 동의·철회(화면 버튼은 없앰, 서버 API 는 남음)·잠금 (A5) ──
const ALLOWED_EXT = /\.(pdf|jpe?g|png)$/i;
export const EVIDENCE_MAX_BYTES = 10 * 1024 * 1024; // inapi FileInterceptor 상한과 같음
// 서버 동의 문구 판(CI-21 v1 = draft-1). 카드 응답에 판이 있으면 그것을 쓴다
export const DEFAULT_TEXT_VERSION = "draft-1";

/** 서면 동의 입력 검사. today = 'YYYY-MM-DD'(KST) */
export const offlineConsentError = (form = {}, today) => {
  const file = form.file;
  if (!file) return "증빙 파일을 올리세요.";
  if (!ALLOWED_EXT.test(file.name || "")) return "증빙은 pdf·jpg·png 만 올릴 수 있습니다.";
  if (file.size > EVIDENCE_MAX_BYTES) return "증빙 파일은 10MB 까지입니다.";
  if (!EVIDENCE_TYPES.some((e) => e.value === form.evidenceType)) return "증빙 종류를 고르세요.";
  if (!trimmed(form.consenterName)) return "동의자 이름을 적으세요.";
  if (!trimmed(form.consenterTitle)) return "동의자 직위를 적으세요.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(form.receivedAt || ""))) return "받은 날을 고르세요.";
  if (today && form.receivedAt > today) return "받은 날은 오늘 이후로 고를 수 없습니다.";
  return "";
};

/**
 * 서면 동의 multipart 칸들([이름, 값]) — 화면이 FormData 로 옮김.
 * inapi 는 DTO 밖 칸이 오면 400(forbidNonWhitelisted) — 계약 칸(OfflineConsentDto)만 보낸다
 */
export const offlineConsentFields = (form, textVersion) => [
  ["file", form.file],
  ["evidenceType", form.evidenceType],
  ["consenterName", trimmed(form.consenterName)],
  ["consenterTitle", trimmed(form.consenterTitle)],
  ["receivedAt", form.receivedAt],
  ["textVersion", textVersion || DEFAULT_TEXT_VERSION],
];

/**
 * 증빙 보기: 콘솔과 같은 출처(blob:)로 열어도 실행되지 않는 형식만 그 형식을 고정해 연다.
 * 그 밖(html·svg 등 — 서버가 다른 형식을 주면)은 null → 화면은 열지 않고 내려받기만(XSS 방지)
 */
const SAFE_EVIDENCE_TYPES = ["application/pdf", "image/png", "image/jpeg"];
export const safeEvidenceType = (type) => {
  const t = String(type || "").split(";")[0].trim().toLowerCase();
  const fixed = t === "image/jpg" ? "image/jpeg" : t;
  return SAFE_EVIDENCE_TYPES.includes(fixed) ? fixed : null;
};

/** 동의 철회 기록 검사: 사유 필수, 서면 철회면 증빙 필수 */
export const revokeError = (form = {}) => {
  if (!trimmed(form.reason)) return "철회 사유를 적으세요.";
  if (form.written && !form.file) return "서면 철회는 증빙 파일을 올리세요.";
  if (form.file && !ALLOWED_EXT.test(form.file.name || "")) return "증빙은 pdf·jpg·png 만 올릴 수 있습니다.";
  if (form.file && form.file.size > EVIDENCE_MAX_BYTES) return "증빙 파일은 10MB 까지입니다.";
  return "";
};

/** 철회 요청: 파일이 있으면 multipart {reason, file}, 없으면 JSON {reason}(OfflineRevokeDto 밖 칸은 400) */
export const revokeRequest = (form) => {
  const reason = trimmed(form.reason);
  if (!form.file) return { json: { reason } };
  return {
    fields: [
      ["reason", reason],
      ["file", form.file],
    ],
  };
};

/** 잠금·풀기 본문 {kind, reason} — 사유는 60자까지(SubAccountLockDto) */
export const LOCK_REASON_MAX = 60;
export const lockError = (reason) =>
  !trimmed(reason) ? "사유를 적으세요." : trimmed(reason).length > LOCK_REASON_MAX ? `사유는 ${LOCK_REASON_MAX}자까지입니다.` : "";
export const lockBody = (reason) => ({ kind: "CERTILIFE", reason: trimmed(reason) });

export const OFFLINE_DONE_TEXT = "등록했습니다.";
export const REVOKE_DONE_TEXT = "기록했습니다. 서브 계정이 즉시 잠기고, 열린 지원 접속은 1분 안에 끝납니다.";

// ── 보안 설정 「기존 회사 일괄 켜기」(사용자 결정 10-29): GET·POST /admin/support/consent-bulk ──
export const BULK_REASON_MAX = 200;
export const bulkError = (reason) => {
  const r = trimmed(reason);
  if (!r) return "사유를 적으세요.";
  return r.length > BULK_REASON_MAX ? `사유는 ${BULK_REASON_MAX}자까지입니다.` : "";
};
export const bulkBody = (reason) => ({ reason: trimmed(reason) });
/** 미리 보기 {companies, noRecord, on, off} → 한 줄 */
export const bulkCountsText = (c) =>
  c ? `고객사 ${c.companies}곳 중 켜짐 ${c.on} · 고객이 끔 ${c.off} · 아직 기록 없음 ${c.noRecord}` : "";
export const bulkDoneText = (r) =>
  `${(r && r.granted) || 0}곳을 켰어요. 고객사에는 메일·이용 내역이 남지 않아요(운영자 이용 내역에만 1건).`;

// ── 위쪽 「지원 접속」 검색(사용자 결정 10-29) ──
export const COMPANY_TYPE_LABEL = {
  HOSPITAL: "병원",
  STUDIO: "STUDIO",
  MANUFACTURER: "제조사",
  DENTAL_LABORATORY: "기공소",
  RESELLER: "리셀러",
};
/** 검색 결과 한 줄 「서티치과 · 병원 · ctdent」 */
export const targetOptionLabel = (c = {}) =>
  [c.name || "(이름 없음)", COMPANY_TYPE_LABEL[c.type] || c.type, c.adminUsername].filter(Boolean).join(" · ");
/** 검색어는 2~50자일 때만 보냄 */
export const searchableQuery = (q) => {
  const t = String(q ?? "").trim();
  return t.length >= 2 && t.length <= 50 ? t : "";
};
